import React from 'react';
import {
  Table as STable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';
import { ColumnDef, flexRender, RowSelectionState, Table } from '@tanstack/react-table';
import { TTreeDevice } from '@/features/devices/schemas/devices.schemas.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useTranslation } from 'react-i18next';

export const DeviceTableContent = ({
  table,
  columns,
  rowSelection,
  setSelectedRows,
  isLoading,
}: {
  table: Table<TTreeDevice>;
  columns: ColumnDef<TTreeDevice>[];
  rowSelection: RowSelectionState;
  setSelectedRows: undefined | React.Dispatch<React.SetStateAction<RowSelectionState>>;
  isLoading?: boolean;
}) => {
  const handleRowClick = (rowId: string) => {
    if (!setSelectedRows) return;

    if (Object.keys(rowSelection)?.[0] === rowId) {
      setSelectedRows({});
      return;
    }

    const newSelection: RowSelectionState = {};
    newSelection[rowId] = true;
    setSelectedRows(newSelection);
  };
  const { t } = useTranslation();

  return (
    <div>
      <div className="relative w-full overflow-auto">
        <STable>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel()?.rows?.length ? (
              table.getRowModel()?.rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => handleRowClick(row.id)}
                  className={`cursor-pointer ${row.getIsSelected() ? 'bg-muted' : ''} hover:bg-muted/50`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {isLoading ? (
                    <div className="flex w-full items-center justify-center py-20">
                      <LoadingSpinner size={50} />
                    </div>
                  ) : (
                    t('common.response.noResults')
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </STable>
      </div>
    </div>
  );
};
